import { httpsCallable } from 'firebase/functions';
import { getFirestore, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import app, { functions } from 'firebase.js'; // Import functions with region
import { usageAnalyticsService } from './usageAnalyticsService';

const db = getFirestore(app);
const auth = getAuth(app);

/**
 * Service for fetching task-focused dashboard analytics
 */
export class DashboardTaskService {
  
  /**
   * Get comprehensive dashboard data including both conversation and task metrics
   */
  async getDashboardData(userId, timeFilter = '7d', agentFilter = 'all') {
    try {
      console.log('📊 DASHBOARD TASK SERVICE: Fetching comprehensive dashboard data');
      
      const timeRange = this.getTimeRange(timeFilter);
      
      // Get user's agents
      const userAgents = await this.getUserAgents(userId);
      const agentIds = userAgents.map(agent => agent.id);
      
      // Create agent lookup map for names
      const agentLookup = {};
      userAgents.forEach(agent => {
        agentLookup[agent.id] = agent.name || `Agent ${agent.id.slice(-8)}`;
      });
      
      if (agentIds.length === 0) {
        console.warn('📊 DASHBOARD TASK SERVICE: No agents found for user');
        return this.getEmptyDashboardData();
      }
      
      // Fetch data in parallel
      const [conversationData, taskData] = await Promise.all([
        this.getConversationMetrics(agentIds, timeRange, agentFilter, agentLookup, userAgents),
        this.getTaskMetrics(agentIds, timeRange, agentFilter, agentLookup)
      ]);
      
      // Combine and return comprehensive dashboard data
      return {
        // Keep the two metrics we want to preserve
        totalCalls: conversationData.totalCalls,
        averageDuration: conversationData.averageDuration,
        
        // New task-focused metrics
        totalTasksCompleted: taskData.totalTasksCompleted,
        tasksCompletedThisWeek: taskData.tasksCompletedThisWeek,
        averageTaskCompletionTime: taskData.averageTaskCompletionTime,
        taskSuccessRate: taskData.taskSuccessRate,
        mostActiveAgent: taskData.mostActiveAgent,
        
        // Enhanced analytics
        tasksByCategory: taskData.tasksByCategory,
        taskCompletionTrends: taskData.taskCompletionTrends,
        toolUsageAnalytics: taskData.toolUsageAnalytics,
        agentTaskPerformance: taskData.agentTaskPerformance,
        
        // Legacy data for backward compatibility (will be phased out)
        recentConversations: conversationData.recentConversations,
        agentStats: conversationData.agentStats,
        
        // Metadata
        timeRange,
        lastUpdated: new Date(),
        agentFilter
      };
      
    } catch (error) {
      console.error('📊 DASHBOARD TASK SERVICE: Error fetching dashboard data:', error);
      throw error;
    }
  }
  
  /**
   * Get conversation metrics (for the two metrics we're keeping) using new usage analytics service
   */
  async getConversationMetrics(agentIds, timeRange, agentFilter, agentLookup, userAgents) {
    try {
      console.log('📊 DASHBOARD TASK SERVICE: Fetching conversation metrics using new usage analytics service');
      
      const targetAgentIds = agentFilter === 'all' ? agentIds : [agentFilter];
      
      // Aggregate meeting metrics across all agents using new usage analytics service
      let totalCalls = 0;
      let totalDurationSeconds = 0;
      const allMeetings = [];
      
      for (const agentId of targetAgentIds) {
        try {
          const meetingAnalytics = await usageAnalyticsService.getMeetingAnalytics(
            agentId,
            timeRange.start,
            timeRange.end
          );
          
          totalCalls += meetingAnalytics.totalCalls || 0;
          totalDurationSeconds += meetingAnalytics.totalDurationSeconds || 0;
          
          console.log(`📊 DASHBOARD TASK SERVICE: Meeting analytics for agent ${agentId}:`, {
            totalCalls: meetingAnalytics.totalCalls,
            totalDurationSeconds: meetingAnalytics.totalDurationSeconds
          });
        } catch (error) {
          console.warn(`📊 DASHBOARD TASK SERVICE: Failed to get meeting analytics for agent ${agentId}:`, error);
          // Continue with other agents
        }
      }
      
      const averageDurationSeconds = totalCalls > 0 ? totalDurationSeconds / totalCalls : 0;
      const averageDuration = this.formatDuration(averageDurationSeconds);
      
      console.log(`📊 DASHBOARD TASK SERVICE: Aggregated conversation metrics:`, {
        totalCalls,
        averageDurationSeconds,
        averageDuration
      });
      
      // Get recent tasks from task-history collection
      const recentTasks = await this.getRecentTasks(userAgents, agentLookup, timeRange);
      
      // Agent stats for backward compatibility (simplified)
      const agentStats = targetAgentIds.map(agentId => ({
        id: agentId,
        name: agentLookup[agentId] || `Agent ${agentId.slice(-8)}`,
        calls: 0, // Will be populated from individual analytics
        minutes: 0,
        cost: 0
      }));
      
      return {
        totalCalls,
        averageDuration,
        recentConversations: recentTasks, // Actually recent tasks now
        agentStats
      };
      
    } catch (error) {
      console.error('📊 DASHBOARD TASK SERVICE: Error fetching conversation metrics:', error);
      throw error;
    }
  }
  
  /**
   * Get comprehensive task metrics using the new usage analytics service
   */
  async getTaskMetrics(agentIds, timeRange, agentFilter, agentLookup) {
    try {
      console.log('📊 DASHBOARD TASK SERVICE: Fetching task metrics using new usage analytics service');
      
      // Get task analytics for each agent using new usage analytics service
      const agentTaskData = [];
      const targetAgentIds = agentFilter === 'all' ? agentIds : [agentFilter];
      
      for (const agentId of targetAgentIds) {
        try {
          // Use new usage analytics service
          const [taskAnalytics, trends] = await Promise.all([
            usageAnalyticsService.getTaskAnalytics(
              agentId,
              timeRange.start,
              timeRange.end
            ),
            usageAnalyticsService.getTaskCompletionTrends(
              agentId,
              timeRange.start,
              timeRange.end,
              'day'
            )
          ]);
          
          const taskData = {
            agentId,
            agentName: agentLookup[agentId] || 'Unknown Agent',
            totalTasks: taskAnalytics.totalTasks || 0,
            successRate: taskAnalytics.successRate || 0,
            avgExecutionTimeMs: taskAnalytics.avgCompletionTimeMs || 0,
            // Map trends data - convert TrendDataPoint[] to format expected by aggregateTaskTrends
            trendsOverTime: trends.map(trend => ({
              date: trend.date instanceof Date ? trend.date.toISOString().split('T')[0] : trend.date,
              totalTasks: trend.totalTasks || 0,
              successfulTasks: trend.successfulTasks || 0,
              failedTasks: trend.failedTasks || 0,
              incompleteTasks: trend.incompleteTasks || 0
            })),
            // Map tool usage - will be fetched separately
            mostUsedTools: [],
            // Map task categories if available (not currently supported)
            taskCategories: []
          };
          
          console.log(`📊 DASHBOARD TASK SERVICE: Task analytics for agent ${agentId}:`, {
            agentName: taskData.agentName,
            totalTasks: taskData.totalTasks,
            successRate: taskData.successRate,
            hasTrendsOverTime: !!(taskData.trendsOverTime?.length > 0),
            trendsOverTimeLength: taskData.trendsOverTime?.length || 0,
            sampleTrend: taskData.trendsOverTime?.[0]
          });
          
          agentTaskData.push(taskData);
        } catch (error) {
          console.warn(`📊 DASHBOARD TASK SERVICE: Failed to get analytics for agent ${agentId}:`, error);
          // Continue with other agents
        }
      }
      
      console.log(`📊 DASHBOARD TASK SERVICE: Got task data for ${agentTaskData.length} agents`);
      
      // Also get tool usage stats and recent activity for all agents
      const [toolUsageStats, recentActivity] = await Promise.all([
        this.getAggregatedToolUsage(agentIds, timeRange, agentFilter),
        this.getRecentTaskActivity(agentIds, timeRange, agentFilter, agentLookup)
      ]);
      
      // Aggregate metrics across all agents
      const aggregatedMetrics = this.aggregateTaskMetrics(agentTaskData, timeRange);
      
      // Enhance with tool usage and recent activity
      aggregatedMetrics.toolUsageAnalytics = toolUsageStats;
      aggregatedMetrics.recentTasks = recentActivity;
      
      return aggregatedMetrics;
      
    } catch (error) {
      console.error('📊 DASHBOARD TASK SERVICE: Error fetching task metrics:', error);
      throw error;
    }
  }
  
  /**
   * Get aggregated tool usage stats across all agents
   */
  async getAggregatedToolUsage(agentIds, timeRange, agentFilter) {
    try {
      const targetAgentIds = agentFilter === 'all' ? agentIds : [agentFilter];
      const toolUsageMap = new Map();
      
      for (const agentId of targetAgentIds) {
        try {
          const toolStats = await usageAnalyticsService.getToolUsageStats(
            agentId,
            timeRange.start,
            timeRange.end,
            10
          );
          
          toolStats.forEach(stat => {
            const existing = toolUsageMap.get(stat.toolName) || {
              tool: stat.toolName,
              count: 0,
              successfulCount: 0
            };
            existing.count += stat.usageCount || 0;
            existing.successfulCount += stat.successfulCount || 0;
            toolUsageMap.set(stat.toolName, existing);
          });
        } catch (error) {
          console.warn(`Failed to get tool usage for agent ${agentId}:`, error);
        }
      }
      
      return Array.from(toolUsageMap.values())
        .map(tool => ({
          ...tool,
          successRate: tool.count > 0 ? (tool.successfulCount / tool.count) * 100 : 0
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    } catch (error) {
      console.error('Error aggregating tool usage:', error);
      return [];
    }
  }
  
  /**
   * Get recent task activity across all agents
   */
  async getRecentTaskActivity(agentIds, timeRange, agentFilter, agentLookup) {
    try {
      const targetAgentIds = agentFilter === 'all' ? agentIds : [agentFilter];
      const allActivities = [];
      
      for (const agentId of targetAgentIds) {
        try {
          const activities = await usageAnalyticsService.getRecentTaskActivity(agentId, 5);
          activities.forEach(activity => {
            // Map TaskActivity format to dashboard format
            allActivities.push({
              id: activity.taskId || activity.recordId,
              agentId,
              agentName: agentLookup[agentId] || 'Unknown Agent',
              status: activity.status === 'successful' ? 'completed' : (activity.status === 'failed' ? 'failed' : 'in_progress'),
              timeAgo: this.getTimeAgo(new Date(activity.timestamp || activity.startTime || activity.createdAt)),
              createdAt: new Date(activity.timestamp || activity.startTime || activity.createdAt),
              duration: this.formatExecutionTime(activity.executionTimeMs || 0),
              taskSummary: (activity.taskDescription || activity.description || 'Task executed').substring(0, 100) + 
                           ((activity.taskDescription || activity.description || 'Task executed').length > 100 ? '...' : ''),
              category: activity.category || 'other'
            });
          });
        } catch (error) {
          console.warn(`Failed to get recent activity for agent ${agentId}:`, error);
        }
      }
      
      // Sort by timestamp and limit
      return allActivities
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        .slice(0, 10);
    } catch (error) {
      console.error('Error getting recent task activity:', error);
      return [];
    }
  }
  
  /**
   * Aggregate task metrics across multiple agents
   */
  aggregateTaskMetrics(agentTaskData, timeRange) {
    if (agentTaskData.length === 0) {
      return this.getEmptyTaskMetrics();
    }
    
    // Aggregate totals
    const totalTasksCompleted = agentTaskData.reduce((sum, agent) => sum + (agent.totalTasks || 0), 0);
    const totalSuccessfulTasks = agentTaskData.reduce((sum, agent) => {
      const successfulTasks = Math.round((agent.totalTasks || 0) * (agent.successRate || 0) / 100);
      return sum + successfulTasks;
    }, 0);
    
    // Calculate overall success rate
    const taskSuccessRate = totalTasksCompleted > 0 ? (totalSuccessfulTasks / totalTasksCompleted) * 100 : 0;
    
    // Calculate average execution time (weighted by task count)
    let totalExecutionTime = 0;
    let totalTasksWithTime = 0;
    
    agentTaskData.forEach(agent => {
      if (agent.avgExecutionTimeMs && agent.totalTasks) {
        totalExecutionTime += agent.avgExecutionTimeMs * agent.totalTasks;
        totalTasksWithTime += agent.totalTasks;
      }
    });
    
    const averageTaskCompletionTime = totalTasksWithTime > 0 
      ? this.formatExecutionTime(totalExecutionTime / totalTasksWithTime)
      : '0s';
    
    // Tasks completed this week
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const tasksCompletedThisWeek = this.calculateTasksInPeriod(agentTaskData, oneWeekAgo, new Date());
    
    // Find most active agent
    const mostActiveAgent = agentTaskData.reduce((most, current) => {
      return (current.totalTasks || 0) > (most.totalTasks || 0) ? current : most;
    }, agentTaskData[0] || {});
    
    // Aggregate task categories
    const tasksByCategory = this.aggregateTaskCategories(agentTaskData);
    
    // Aggregate tool usage
    const toolUsageAnalytics = this.aggregateToolUsage(agentTaskData);
    
    // Task completion trends
    const taskCompletionTrends = this.aggregateTaskTrends(agentTaskData);
    
    // Agent performance comparison
    const agentTaskPerformance = agentTaskData.map(agent => ({
      agentId: agent.agentId,
      agentName: agent.agentName,
      totalTasks: agent.totalTasks || 0,
      successRate: agent.successRate || 0,
      avgExecutionTime: agent.avgExecutionTimeMs || 0,
      mostUsedTools: (agent.mostUsedTools || []).slice(0, 3)
    })).sort((a, b) => b.totalTasks - a.totalTasks);
    
    return {
      totalTasksCompleted,
      tasksCompletedThisWeek,
      averageTaskCompletionTime,
      taskSuccessRate,
      mostActiveAgent: {
        agentId: mostActiveAgent.agentId,
        agentName: mostActiveAgent.agentName || 'No agents',
        totalTasks: mostActiveAgent.totalTasks || 0
      },
      tasksByCategory,
      taskCompletionTrends,
      toolUsageAnalytics,
      agentTaskPerformance
    };
  }
  
  /**
   * Get user's agents
   */
  async getUserAgents(userId) {
    try {
      const agentsQuery = query(
        collection(db, 'agents'), 
        where('admins', 'array-contains', userId)
      );
      const agentsSnapshot = await getDocs(agentsQuery);
      
      return agentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('📊 DASHBOARD TASK SERVICE: Error fetching user agents:', error);
      return [];
    }
  }
  
  /**
   * Helper methods
   */
  getTimeRange(timeFilter) {
    const end = new Date();
    const start = new Date();
    
    switch (timeFilter) {
      case '24h':
        start.setDate(start.getDate() - 1);
        break;
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
      default:
        start.setDate(start.getDate() - 7);
    }
    
    return { start, end };
  }
  
  formatDuration(durationSecs) {
    // Round to avoid decimal issues and match conversationHelpers.js exactly
    const roundedSecs = Math.round(durationSecs);
    
    if (roundedSecs < 60) {
      return `${roundedSecs}s`;
    }
    
    // For durations over 1 hour, show hours
    if (roundedSecs >= 3600) {
      const hours = Math.floor(roundedSecs / 3600);
      const minutes = Math.floor((roundedSecs % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
    
    const minutes = Math.floor(roundedSecs / 60);
    const seconds = roundedSecs % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
  
  formatExecutionTime(timeMs) {
    if (!timeMs) return '0s';
    if (timeMs < 1000) return `${Math.round(timeMs)}ms`;
    if (timeMs < 60000) return `${(timeMs / 1000).toFixed(1)}s`;
    return `${(timeMs / 60000).toFixed(1)}m`;
  }
  
  getTimeAgo(date) {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  }
  
  getConversationStatus(conv) {
    if (conv.analysis?.call_successful === 'success') return 'success';
    if (conv.status === 'failed') return 'failed';
    return 'completed';
  }
  
  calculateAgentStats(conversations) {
    const agentStats = {};
    
    conversations.forEach(conv => {
      const agentId = conv.blueprintAgentId || conv.agentId;
      if (!agentId) return;
      
      if (!agentStats[agentId]) {
        agentStats[agentId] = {
          id: agentId,
          name: conv.agentName || `Agent ${agentId.slice(-8)}`,
          calls: 0,
          minutes: 0,
          cost: 0
        };
      }
      
      agentStats[agentId].calls++;
      agentStats[agentId].minutes += (conv.metadata?.call_duration_secs || 0) / 60;
      agentStats[agentId].cost += conv.metadata?.cost || 0;
    });
    
    return Object.values(agentStats)
      .map(agent => ({
        ...agent,
        minutes: agent.minutes.toFixed(1),
        cost: `$${agent.cost.toFixed(2)}`
      }))
      .sort((a, b) => b.calls - a.calls)
      .slice(0, 5);
  }
  
  calculateTasksInPeriod(agentTaskData, startDate, endDate) {
    // This would need to be implemented based on the task trends data
    // For now, we'll estimate based on recent trends
    return agentTaskData.reduce((sum, agent) => {
      const recentTrends = (agent.trendsOverTime || []).filter(trend => {
        const trendDate = new Date(trend.date);
        return trendDate >= startDate && trendDate <= endDate;
      });
      
      const weeklyTasks = recentTrends.reduce((weekSum, trend) => weekSum + (trend.totalTasks || 0), 0);
      return sum + weeklyTasks;
    }, 0);
  }
  
  aggregateTaskCategories(agentTaskData) {
    const categoryMap = new Map();
    
    agentTaskData.forEach(agent => {
      (agent.taskCategories || []).forEach(category => {
        const existing = categoryMap.get(category.category) || { category: category.category, count: 0, successfulCount: 0 };
        existing.count += category.count;
        existing.successfulCount += Math.round(category.count * category.successRate / 100);
        categoryMap.set(category.category, existing);
      });
    });
    
    return Array.from(categoryMap.values())
      .map(cat => ({
        ...cat,
        successRate: cat.count > 0 ? (cat.successfulCount / cat.count) * 100 : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }
  
  aggregateToolUsage(agentTaskData) {
    const toolMap = new Map();
    
    agentTaskData.forEach(agent => {
      (agent.mostUsedTools || []).forEach(tool => {
        const existing = toolMap.get(tool.tool) || { tool: tool.tool, count: 0, successfulCount: 0 };
        existing.count += tool.count;
        existing.successfulCount += Math.round(tool.count * tool.successRate / 100);
        toolMap.set(tool.tool, existing);
      });
    });
    
    return Array.from(toolMap.values())
      .map(tool => ({
        ...tool,
        successRate: tool.count > 0 ? (tool.successfulCount / tool.count) * 100 : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }
  
  aggregateTaskTrends(agentTaskData) {
    console.log('📊 DASHBOARD TASK SERVICE: Aggregating task trends from agents:', {
      agentCount: agentTaskData.length,
      agentsWithTrends: agentTaskData.filter(a => a.trendsOverTime?.length > 0).length,
      sampleAgent: agentTaskData[0] ? {
        agentName: agentTaskData[0].agentName,
        hasTrends: !!(agentTaskData[0].trendsOverTime),
        trendsLength: agentTaskData[0].trendsOverTime?.length || 0
      } : null
    });
    
    const trendMap = new Map();
    
    agentTaskData.forEach(agent => {
      (agent.trendsOverTime || []).forEach(trend => {
        // Handle both date string format and object format
        const dateKey = typeof trend === 'string' ? trend : (trend.date || trend.timestamp);
        
        if (!dateKey) {
          console.warn('📊 DASHBOARD TASK SERVICE: Trend entry missing date:', trend);
          return;
        }
        
        const existing = trendMap.get(dateKey) || { 
          date: dateKey, 
          totalTasks: 0, 
          successfulTasks: 0, 
          totalExecutionTime: 0,
          taskCount: 0
        };
        
        existing.totalTasks += (trend.totalTasks || trend.count || 0);
        existing.successfulTasks += (trend.successfulTasks || trend.successfulCount || 0);
        
        if (trend.avgExecutionTimeMs || trend.avgExecutionTime) {
          const avgTime = trend.avgExecutionTimeMs || trend.avgExecutionTime;
          const taskCount = trend.totalTasks || trend.count || 0;
          existing.totalExecutionTime += avgTime * taskCount;
          existing.taskCount += taskCount;
        }
        
        trendMap.set(dateKey, existing);
      });
    });
    
    const finalTrends = Array.from(trendMap.values())
      .map(trend => ({
        ...trend,
        avgExecutionTimeMs: trend.taskCount > 0 ? trend.totalExecutionTime / trend.taskCount : 0
      }))
      .sort((a, b) => {
        // Sort by date string comparison
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA.getTime() - dateB.getTime();
      })
      .slice(-30); // Last 30 days
      
    console.log('📊 DASHBOARD TASK SERVICE: Final task trends:', {
      trendsCount: finalTrends.length,
      sampleTrends: finalTrends.slice(0, 3),
      dateRange: finalTrends.length > 0 ? {
        first: finalTrends[0]?.date,
        last: finalTrends[finalTrends.length - 1]?.date
      } : null
    });
      
    return finalTrends;
  }
  
  getEmptyDashboardData() {
    return {
      totalCalls: 0,
      averageDuration: '0s',
      totalTasksCompleted: 0,
      tasksCompletedThisWeek: 0,
      averageTaskCompletionTime: '0s',
      taskSuccessRate: 0,
      mostActiveAgent: { agentId: null, agentName: 'No agents', totalTasks: 0 },
      tasksByCategory: [],
      taskCompletionTrends: [],
      toolUsageAnalytics: [],
      agentTaskPerformance: [],
      recentConversations: [],
      agentStats: [],
      timeRange: this.getTimeRange('7d'),
      lastUpdated: new Date(),
      agentFilter: 'all'
    };
  }
  
  getEmptyTaskMetrics() {
    return {
      totalTasksCompleted: 0,
      tasksCompletedThisWeek: 0,
      averageTaskCompletionTime: '0s',
      taskSuccessRate: 0,
      mostActiveAgent: { agentId: null, agentName: 'No agents', totalTasks: 0 },
      tasksByCategory: [],
      taskCompletionTrends: [],
      toolUsageAnalytics: [],
      agentTaskPerformance: []
    };
  }

  // Get recent tasks from taskHistory collection (matching Task History page)
  async getRecentTasks(userAgents, agentLookup, timeRange) {
    try {
      const db = getFirestore();
      const agentIds = userAgents.map(agent => agent.id);
      
      // Query taskHistory collection for recent tasks with time filtering
      console.log('📊 DASHBOARD TASK SERVICE: Fetching recent tasks with time filter:', {
        timeRange: {
          start: timeRange.start.toISOString(),
          end: timeRange.end.toISOString()
        },
        agentCount: agentIds.length
      });
      
      const tasksQuery = query(
        collection(db, 'taskHistory'),
        where('agentId', 'in', agentIds),
        where('createdAt', '>=', timeRange.start),
        where('createdAt', '<=', timeRange.end),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      
      const tasksSnapshot = await getDocs(tasksQuery);
      const tasks = tasksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      return tasks.map(task => {
        // Calculate task execution duration (matching TaskHistoryPanel format)
        let duration = 'Unknown';
        if (task.executionTimeMs) {
          const timeMs = task.executionTimeMs;
          if (timeMs < 1000) {
            duration = `${timeMs}ms`;
          } else if (timeMs < 60000) {
            duration = `${(timeMs / 1000).toFixed(1)}s`;
          } else {
            duration = `${(timeMs / 60000).toFixed(1)}m`;
          }
        }

        // Get task description (matching TaskHistoryPanel structure)
        const taskSummary = task.taskDescription || task.description || 'Task executed';

        // Determine task status (matching TaskHistoryPanel logic)
        let status = 'completed';
        if (task.status === 'failed') {
          status = 'failed';
        } else if (task.status === 'running') {
          status = 'in_progress';
        } else if (task.status === 'pending') {
          status = 'in_progress';
        } else if (task.status === 'completed' && task.success === false) {
          status = 'failed';
        }

        // Convert Firebase timestamp to Date for timeAgo calculation
        const createdAt = task.createdAt?.toDate ? task.createdAt.toDate() : new Date(task.createdAt || Date.now());

        return {
          id: task.id,
          agentName: agentLookup[task.agentId] || 'Unknown Agent', // Use agentId field like TaskHistoryPanel
          status,
          timeAgo: this.getTimeAgo(createdAt),
          createdAt,
          duration,
          taskSummary: taskSummary.length > 100 ? taskSummary.substring(0, 100) + '...' : taskSummary,
          category: task.category || 'other'
        };
      });
      
    } catch (error) {
      console.error('📊 DASHBOARD TASK SERVICE: Error fetching recent tasks:', error);
      return [];
    }
  }

  // Get dashboard data for a specific agent
  async getAgentDashboardData(userId, agentId, timeFilter = '7d') {
    try {
      console.log('📊 DASHBOARD TASK SERVICE: Fetching agent dashboard data for:', agentId);
      
      // Get subscription to use period dates for metrics
      const subscriptionResponse = await fetch(
        `https://us-central1-polletask-dev.cloudfunctions.net/billing-getSubscriptionHttp?agentId=${agentId}`,
        {
          headers: {
            'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
          }
        }
      );
      const subscriptionResult = await subscriptionResponse.json();
      
      // Use subscription period dates if available, otherwise fall back to timeFilter
      let startDate, endDate;
      if (subscriptionResult.success && subscriptionResult.subscription) {
        startDate = new Date(subscriptionResult.subscription.currentPeriodStart);
        endDate = new Date(subscriptionResult.subscription.currentPeriodEnd);
        console.log('📊 DASHBOARD TASK SERVICE: Using subscription period dates:', {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
      } else {
        const timeRange = this.getTimeRange(timeFilter);
        startDate = timeRange.start;
        endDate = timeRange.end;
        console.log('📊 DASHBOARD TASK SERVICE: Using timeFilter dates:', {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
      }
      
      // Get user's agents to create lookup
      const userAgents = await this.getUserAgents(userId);
      const agentLookup = {};
      userAgents.forEach(agent => {
        agentLookup[agent.id] = agent.name || `Agent ${agent.id.slice(-8)}`;
      });
      
      // Filter for just this agent
      const targetAgent = userAgents.find(agent => agent.id === agentId);
      if (!targetAgent) {
        throw new Error('Agent not found');
      }
      
      // Fetch data using new usage analytics service with subscription period dates
      const [dashboardMetrics, meetingAnalytics] = await Promise.all([
        usageAnalyticsService.getDashboardMetrics(agentId, startDate, endDate),
        usageAnalyticsService.getMeetingAnalytics(agentId, startDate, endDate)
      ]);
      
      // Combine data for single agent
      return {
        totalCalls: dashboardMetrics.totalCalls || meetingAnalytics.totalCalls || 0,
        averageDuration: this.formatDuration(dashboardMetrics.avgCallDurationSeconds || 0),
        formattedAvgDuration: this.formatDuration(dashboardMetrics.avgCallDurationSeconds || 0),
        
        // Task metrics
        totalTasks: dashboardMetrics.totalTasks || 0,
        taskSuccessRate: dashboardMetrics.taskSuccessRate || 0,
        formattedTaskSuccessRate: `${(dashboardMetrics.taskSuccessRate || 0).toFixed(1)}%`,
        averageTaskCompletionTime: this.formatExecutionTime(dashboardMetrics.avgTaskCompletionTimeMs || 0),
        
        // Conversation analytics (simplified for now)
        peakHour: 14, // Default, can be enhanced later
        busiestDay: 2, // Default, can be enhanced later
        
        // Agent info
        agentName: agentLookup[agentId] || 'Unknown Agent'
      };
      
    } catch (error) {
      console.error('📊 DASHBOARD TASK SERVICE: Error fetching agent dashboard data:', error);
      throw error;
    }
  }

  // Get conversation analytics for a specific agent
  async getAgentConversationAnalytics(agentId, timeRange) {
    try {
      console.log('📊 DASHBOARD TASK SERVICE: Fetching conversation analytics for agent:', agentId);
      
      const db = getFirestore();
      
      // Query conversations for this specific agent within time range
      const conversationsQuery = query(
        collection(db, 'conversations-v2'),
        where('blueprintAgentId', '==', agentId),
        where('createdAt', '>=', timeRange.start),
        where('createdAt', '<=', timeRange.end),
        orderBy('createdAt', 'desc')
      );
      
      const conversationsSnapshot = await getDocs(conversationsQuery);
      const conversations = conversationsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          startTime: data.startTime?.toDate ? data.startTime.toDate() : data.startTime,
          endTime: data.endTime?.toDate ? data.endTime.toDate() : data.endTime,
        };
      });
      
      console.log(`📊 DASHBOARD TASK SERVICE: Found ${conversations.length} conversations for analytics`);
      
      // Calculate peak hour (0-23)
      const hourCounts = {};
      conversations.forEach(conv => {
        if (conv.startTime) {
          const hour = new Date(conv.startTime).getHours();
          hourCounts[hour] = (hourCounts[hour] || 0) + 1;
        }
      });
      
      const peakHour = Object.keys(hourCounts).reduce((peak, hour) => {
        return hourCounts[hour] > (hourCounts[peak] || 0) ? hour : peak;
      }, 14); // Default to 2 PM if no data
      
      // Calculate busiest day (0=Sunday, 1=Monday, etc.)
      const dayCounts = {};
      conversations.forEach(conv => {
        if (conv.startTime) {
          const day = new Date(conv.startTime).getDay();
          dayCounts[day] = (dayCounts[day] || 0) + 1;
        }
      });
      
      const busiestDay = Object.keys(dayCounts).reduce((busiest, day) => {
        return dayCounts[day] > (dayCounts[busiest] || 0) ? day : busiest;
      }, 2); // Default to Tuesday if no data
      
      console.log('📊 DASHBOARD TASK SERVICE: Conversation analytics:', {
        totalConversations: conversations.length,
        peakHour: parseInt(peakHour),
        busiestDay: parseInt(busiestDay),
        hourDistribution: hourCounts,
        dayDistribution: dayCounts
      });
      
      return {
        peakHour: parseInt(peakHour),
        busiestDay: parseInt(busiestDay)
      };
      
    } catch (error) {
      console.error('📊 DASHBOARD TASK SERVICE: Error fetching conversation analytics:', error);
      return {
        peakHour: 14, // Default to 2 PM
        busiestDay: 2  // Default to Tuesday
      };
    }
  }
}

// Export singleton instance
export const dashboardTaskService = new DashboardTaskService();
